import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

function DetectSpeedTest(props) {
  const onSummary = props.onSummary;

  // test URL can be provided at build/runtime via REACT_APP_SPEEDTEST_URL
  const defaultUrl = process.env.REACT_APP_SPEEDTEST_URL || "/download-test/test_file.zip";
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const runs = 1;

  const reset = useCallback(() => {
    setProgress(0);
    setResults([]);
    setRunning(false);
  }, []);

  useEffect(() => {
    if (results.length > 0) {
      const avg =
        results.reduce((s, r) => s + r.downloadMbps, 0) / results.length;
      onSummary(DetectSpeedTest, { averageDownloadMbps: avg, runs: results });
    } else {
      onSummary(DetectSpeedTest, { averageDownloadMbps: null, runs: [] });
    }
  }, [results, onSummary]);

  const runSingleDownload = async (url, timeoutMs = 60000) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    const start = performance.now();
    let bytes = 0;

    try {
      const res = await fetch(url + (url.includes("?") ? "&" : "?") + "_=" + Date.now(), {
        method: "GET",
        signal: controller.signal,
        cache: "no-store"
      });

      if (!res.ok) throw new Error("Download failed: " + res.status);

      const reader = res.body && res.body.getReader();
      if (!reader) throw new Error("Stream reader not available");

      const contentLength = parseInt(res.headers.get('content-length') || '0', 10);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        bytes += value.length;
        if (contentLength > 0) {
          const percentComplete = Math.round((bytes / contentLength) * 100);
          setProgress(Math.min(99, percentComplete));
        }
      }

      const end = performance.now();
      clearTimeout(timeout);
      const seconds = Math.max(0.001, (end - start) / 1000);
      const bits = bytes * 8;
      const mbps = bits / seconds / 1e6;
      return { success: true, bytes, seconds, downloadMbps: Number(mbps.toFixed(2)) };
    } catch (err) {
      clearTimeout(timeout);
      let message = err && err.message ? err.message : String(err);
      const isTimeout = message === 'BodyStreamBuffer was aborted';
      if (isTimeout) {
        message = `Timeout after ${timeoutMs / 1000}s — the connection is too slow or the server did not respond quickly enough.`;
      }
      return { success: false, error: message, isTimeout };
    }
  };

  const startTest = async () => {
    setRunning(true);
    setResults([]);
    setProgress(0);

    for (let i = 0; i < runs; i++) {
      setProgress(Math.round((i / runs) * 50));
      const r = await runSingleDownload(defaultUrl);
      if (r.success) {
        setResults((prev) => [...prev, r]);
      } else {
        setResults((prev) => [...prev, { ...r, downloadMbps: 0 }]);
      }
      setProgress(Math.round(((i + 1) / runs) * 100));
    }

    setRunning(false);
    setProgress(100);
  };

  const avgMbps = results.length
    ? (results.reduce((s, r) => s + r.downloadMbps, 0) / results.length).toFixed(2)
    : "-";

  const lastResult = results.length ? results[results.length - 1] : null;

  return (
    <div className="nogap">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span >Internet Speed Test</span>
          
        </div>

        <div>
          <button onClick={startTest} disabled={running} style={{ padding: '5px 5px' }}>
            {running ? 'Running…' : 'Start Test'}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        {running && (
          <>
            <div style={{ color: '#0ea5e9' }}>Testing… {progress}%</div>
            <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden', marginTop: 6 }}>
              <div style={{ width: `${progress}%`, height: '100%', background: '#0ea5e9', transition: 'width 0.2s ease' }} />
            </div>
          </>
        )}
        {!running && lastResult && lastResult.success && (
          <div style={{ fontWeight: 600 }}>Download: {lastResult.downloadMbps} Mbps</div>
        )}
        {!running && lastResult && !lastResult.success && (
          <div>
            <div style={{ color: '#b91c1c' }}>Error: {lastResult.error}</div>

          </div>
        )}
        {!running && !lastResult && (
          <div style={{ color: '#666' }}>Press Start Test to measure your download speed.</div>
        )}
      </div>
    </div>
  );
}

DetectSpeedTest.propTypes = {
  onSummary: PropTypes.func.isRequired,
};

export default DetectSpeedTest;
